"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parameterArrayFromGroups = parameterArrayFromGroups;
exports.flattenData = flattenData;
exports.unflattenData = unflattenData;
exports.joiSchema = joiSchema;
const CustomJoi_1 = require("./CustomJoi");
const joi_1 = __importDefault(require("joi"));
const lodash_1 = __importDefault(require("lodash"));
/**
 * Extract all parameters in the form of parameter array from parameter form
 * Useful for validating  parameter form
 * @param groups List of parameter groups
 */
function parameterArrayFromGroups(groups) {
    return groups.reduce((params, group) => [...params, ...group.parameters], []);
}
function flattenData(params, data) {
    const clonedData = lodash_1.default.cloneDeep(data);
    let flatData = {};
    // Iterate through each parameter and set the required values in flatData
    // Return the each of the keys used in the process
    const keysUsed = params.map((param) => {
        const { id } = param;
        const value = lodash_1.default.get(clonedData, id);
        if (param.type === 'json_array' && param.items) {
            // For each item in the array, set the corresponding value in the flatData
            param.items.forEach((item, index) => {
                const itemId = `${id}.${item.id}`;
                const itemPath = `${id}.${index}`;
                flatData[itemId] = lodash_1.default.get(clonedData, itemPath);
            });
        }
        if (value !== undefined) {
            flatData[id] = value;
        }
        return id;
    });
    // Remove the properties used from the cloned data
    keysUsed.forEach((key) => {
        lodash_1.default.unset(clonedData, key);
    });
    // Add the remaining (potentially unflattenData) data to the flatData
    flatData = Object.assign(Object.assign({}, flatData), clonedData);
    return flatData;
}
function unflattenData(params, flatData) {
    const data = {};
    params.forEach((param) => {
        const { id } = param;
        let value = flatData[id];
        if (param.type === 'json_array' && param.items) {
            // Reconstruct the array
            value = param.items.map((item) => lodash_1.default.get(flatData, `${id}.${item.id}`));
        }
        if (value !== undefined) {
            lodash_1.default.set(data, id, value);
        }
    });
    return data;
}
/**
 * Converts a parameter array to a Joi schema that can be used to
 * validate an object of that type.
 * @param params List of parameters
 */
function joiSchema(params, options = {}) {
    const schemaMap = {};
    params.forEach((param) => {
        var _a, _b, _c, _d, _e, _f;
        const { id, label, description, required } = param;
        let base;
        switch (param.type) {
            case 'string': {
                const defaultRegex = /^[^\p{Cc}<>"%`$&|\\]*$/u;
                const validationRegex = param.pattern ? new RegExp(param.pattern, 'u') : defaultRegex;
                const defaultMaxLength = param.long ? 500 : 250;
                base = CustomJoi_1.CustomJoi.string()
                    .empty('')
                    .regex(validationRegex)
                    .max((_a = param.maxLength) !== null && _a !== void 0 ? _a : defaultMaxLength);
                if (param.stripHtml === true || param.stripHtml === undefined) {
                    base = base.htmlStrip();
                }
                break;
            }
            case 'boolean':
                base = joi_1.default.boolean().default(false);
                break;
            case 'int':
                base = joi_1.default.number()
                    .integer()
                    .min((_b = param.min) !== null && _b !== void 0 ? _b : Number.MIN_SAFE_INTEGER)
                    .max((_c = param.max) !== null && _c !== void 0 ? _c : Number.MAX_SAFE_INTEGER);
                break;
            case 'float':
                base = joi_1.default.number()
                    .min((_d = param.min) !== null && _d !== void 0 ? _d : Number.MIN_SAFE_INTEGER)
                    .max((_e = param.max) !== null && _e !== void 0 ? _e : Number.MAX_SAFE_INTEGER);
                break;
            case 'enum': {
                const values = Object.keys(param.list);
                base = joi_1.default.string()
                    .valid(...values)
                    .empty('');
                break;
            }
            case 'enum_multi': {
                const values = Object.keys(param.list);
                base = joi_1.default.array().items(joi_1.default.string()
                    .valid(...values)
                    .empty(''));
                break;
            }
            case 'list':
                base = joi_1.default.array().items(joi_1.default.string()).empty(joi_1.default.array().length(0));
                break;
            case 'time':
                base = joi_1.default.string()
                    .regex(/^([01]\d|2[0-3]):?([0-5]\d)$/)
                    .empty('');
                break;
            case 'date':
                base = joi_1.default.string()
                    .regex(/^\d\d\d\d-\d\d-\d\d$/)
                    .empty('');
                break;
            case 'file':
                base = joi_1.default.any();
                break;
            case 'json_object': {
                base = joi_1.default.object();
                break;
            }
            case 'json_array':
                if (param.items) {
                    base = joiSchema(param.items, {
                        isKeyPath: options.isKeyPath,
                        schemaType: 'ARRAY',
                    });
                }
                else {
                    base = joi_1.default.array().items(joi_1.default.any());
                }
                break;
            case 'json':
                base = joi_1.default.alternatives(joi_1.default.object(), joi_1.default.array().items(joi_1.default.any()));
                break;
            case 'multi_file':
                base = joi_1.default.array().items(joi_1.default.any());
                break;
            case 'fixed':
                base = joi_1.default.any().default(param.value);
                break;
        }
        base = base.label(label);
        if (description) {
            base.description(description);
        }
        if ('default' in param && param.default) {
            base = base.default(param.default);
        }
        if (param.valid) {
            base = base.valid(...param.valid);
        }
        if (required) {
            base = base.required();
        }
        if ('requires' in param && param.requires) {
            // Build the schema for marking the field as required or optional
            const requiredIfSatisfied = 'requiredIfSatisfied' in param && param.requiredIfSatisfied;
            if (requiredIfSatisfied) {
                base = base.required();
            }
            for (const requirement of param.requires) {
                const [key, value, type = 'EQUALS'] = requirement;
                let requirementSchema = joi_1.default.any();
                let isUndefinedExpected = false;
                switch (type) {
                    case 'EQUALS':
                        if (value === undefined) {
                            isUndefinedExpected = true;
                            break;
                        }
                        if (value instanceof Array) {
                            throw new Error('Array values are not supported for EQUALS requirement. Try using IN or NOT_IN');
                        }
                        requirementSchema = joi_1.default.valid(value);
                        // Treat an absent key the same as `false`, mirroring the renderer which
                        // considers an undefined field equal to false for boolean requirements.
                        // Without this, the condition would only match when the key is explicitly
                        // set to false, so dependent fields wouldn't be validated on first render.
                        if (value === false) {
                            isUndefinedExpected = true;
                        }
                        break;
                    case 'NOT_EQUALS':
                        if (value === undefined) {
                            isUndefinedExpected = true;
                            break;
                        }
                        if (value instanceof Array) {
                            throw new Error('Array values are not supported for EQUALS requirement. Try using IN or NOT_IN');
                        }
                        requirementSchema = joi_1.default.invalid(value);
                        break;
                    case 'IN':
                    case 'NOT_IN': {
                        if (!(value instanceof Array)) {
                            throw new Error('IN requirement expects an array of values');
                        }
                        // Handle undefined values in the array
                        const definedValues = value.filter((v) => {
                            if (v !== undefined) {
                                return true;
                            }
                            isUndefinedExpected = true;
                            return false;
                        });
                        // Apply the requirement based on the type (IN or NOT_IN)
                        if (type === 'IN') {
                            requirementSchema = joi_1.default.valid(...definedValues);
                        }
                        else {
                            requirementSchema = joi_1.default.invalid(...definedValues);
                        }
                        break;
                    }
                    default:
                        throw new Error(`Unsupported requirement type: ${type}`);
                }
                if (!isUndefinedExpected) {
                    // Mark the condition field as required
                    // Don't mark the field as required if the expected value is undefined
                    // NOTE: This ensures the condition fails when the key is not present in the object.
                    requirementSchema = requirementSchema.required();
                }
                base = joi_1.default.when(`/${key}`, {
                    is: requirementSchema,
                    then: base,
                    otherwise: joi_1.default.optional().strip(),
                });
            }
        }
        if (options.isKeyPath) {
            const existingSchema = lodash_1.default.get(schemaMap, id);
            if (existingSchema) {
                const strategy = (_f = options.schemaOverlapResolution) !== null && _f !== void 0 ? _f : 'OVERWRITE';
                switch (strategy) {
                    case 'OVERWRITE':
                        // Ignore the existing schema and overwrite it
                        break;
                    case 'CONCAT':
                        base = base.concat(existingSchema);
                        break;
                    case 'ALTERNATE':
                        base = joi_1.default.alternatives(existingSchema, base);
                        break;
                }
            }
            lodash_1.default.set(schemaMap, id, base);
        }
        else {
            schemaMap[id] = base;
        }
    });
    if (options.schemaType && options.schemaType === 'ARRAY') {
        const values = Object.values(schemaMap);
        // @ts-expect-error -- type cast for Joi array schema
        return joi_1.default.array().ordered(...values);
    }
    // @ts-expect-error -- type case for Joi object schema
    return joi_1.default.object(schemaMap);
}
//# sourceMappingURL=Index.js.map